import * as React from 'react';
import {
    Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, InputLabel, MenuItem, Pagination, Select, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { createVirtualTeam, getAvailableVirtualPlayers, previewVirtualTeam } from '../../Api';
import {
    GeneratedVirtualTeamDto, VirtualLineupType, VirtualPoolPlayerAssignment, VirtualProviderPlayerDto,
    VirtualTeamDraftDto,
} from '../../models/dtos/VirtualProviderCatalogDto';
import { VIRTUAL_FOOTBALL_ID, VirtualCatalogSportDto } from '../../models/dtos/VirtualSportsCatalogDto';
import VirtualCountrySelect from './VirtualCountrySelect';
import {
    VIRTUAL_LINEUP_TYPES, VIRTUAL_TEAM_ROSTER_PLANS, virtualLineupLabel, virtualRosterMaximum,
} from './VirtualTeamRoster';

const EMPTY_DRAFT: VirtualTeamDraftDto = { name: '', nameRu: '', biography: '', biographyRu: '', originCountry: '' };
const PLAYER_POOL_PAGE_SIZE = 10;

export default function VirtualTeamCreationDialog(props: {
    open: boolean;
    sports: VirtualCatalogSportDto[];
    onClose: () => void;
    onCreated: (team: GeneratedVirtualTeamDto) => void;
}) {
    const [sportId, setSportId] = React.useState<number | ''>('');
    const [draft, setDraft] = React.useState<VirtualTeamDraftDto>(EMPTY_DRAFT);
    const [pool, setPool] = React.useState<VirtualProviderPlayerDto[]>([]);
    const [poolPage, setPoolPage] = React.useState(0);
    const [poolTotal, setPoolTotal] = React.useState(0);
    const [assignments, setAssignments] = React.useState<Record<string, VirtualLineupType>>({});
    const [generationDescription, setGenerationDescription] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [loadingPool, setLoadingPool] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        if (!props.open) return;
        setSportId(props.sports.some(sport => sport.id === VIRTUAL_FOOTBALL_ID) ? VIRTUAL_FOOTBALL_ID : '');
        setDraft(EMPTY_DRAFT); setPool([]); setPoolPage(0); setPoolTotal(0); setAssignments({});
        setGenerationDescription(''); setError('');
    }, [props.open, props.sports]);

    React.useEffect(() => {
        if (!props.open || sportId === '') {
            setLoadingPool(false);
            return;
        }
        let cancelled = false;
        setLoadingPool(true);
        getAvailableVirtualPlayers(sportId, poolPage * PLAYER_POOL_PAGE_SIZE, PLAYER_POOL_PAGE_SIZE)
            .then(page => {
                if (cancelled) return;
                setPool(page.items);
                setPoolTotal(page.totalItems);
            })
            .catch(reason => {
                if (cancelled) return;
                setError(reason instanceof Error ? reason.message : 'Could not load player pool');
                setPool([]);
                setPoolTotal(0);
            })
            .finally(() => { if (!cancelled) setLoadingPool(false); });
        return () => { cancelled = true; };
    }, [props.open, sportId, poolPage]);

    const selectSport = (id: number) => {
        setSportId(id); setDraft(EMPTY_DRAFT); setPool([]); setPoolPage(0); setPoolTotal(0); setAssignments({});
        setGenerationDescription(''); setError('');
    };
    const poolPlayers = (): VirtualPoolPlayerAssignment[] => Object.entries(assignments)
        .map(([playerId, lineupType]) => ({ playerId, lineupType }));
    const togglePlayer = (playerId: string) => {
        setAssignments(current => {
            const next = { ...current };
            if (next[playerId]) delete next[playerId]; else next[playerId] = 'STARTING_LINEUP';
            return next;
        });
    };
    const changeLineupType = (playerId: string, lineupType: VirtualLineupType) => {
        setAssignments(current => ({ ...current, [playerId]: lineupType }));
    };
    const update = (field: 'name' | 'nameRu' | 'biography' | 'biographyRu' | 'originCountry', value: string) =>
        setDraft(current => ({ ...current, [field]: value }));
    const run = async (action: () => Promise<void>) => {
        setBusy(true); setError('');
        try { await action(); } catch (reason) { setError(reason instanceof Error ? reason.message : 'Request failed'); }
        finally { setBusy(false); }
    };
    const rosterPlan = sportId === '' ? null : VIRTUAL_TEAM_ROSTER_PLANS[sportId];
    const assignmentCounts = VIRTUAL_LINEUP_TYPES.reduce<Record<VirtualLineupType, number>>((counts, role) => {
        counts[role] = Object.values(assignments).filter(value => value === role).length;
        return counts;
    }, { STARTING_LINEUP: 0, SUBSTITUTES: 0, COACH: 0 });
    const rosterWithinLimits = rosterPlan != null && VIRTUAL_LINEUP_TYPES.every(
        role => assignmentCounts[role] <= virtualRosterMaximum(rosterPlan, role),
    );
    const rosterComplete = rosterPlan != null
        && assignmentCounts.STARTING_LINEUP === rosterPlan.startingLineup
        && assignmentCounts.SUBSTITUTES >= rosterPlan.minimumSubstitutes
        && assignmentCounts.COACH === rosterPlan.coaches;
    const randomizeRoster = async () => {
        if (!rosterPlan || sportId === '' || poolTotal === 0) return;
        setLoadingPool(true);
        setError('');
        try {
            const candidatePage = await getAvailableVirtualPlayers(sportId, 0, 100);
            const randomizedPlayers = [...candidatePage.items];
            for (let index = randomizedPlayers.length - 1; index > 0; index -= 1) {
                const randomIndex = Math.floor(Math.random() * (index + 1));
                [randomizedPlayers[index], randomizedPlayers[randomIndex]] = [randomizedPlayers[randomIndex], randomizedPlayers[index]];
            }
            const roles = [
                ...Array(rosterPlan.startingLineup).fill('STARTING_LINEUP'),
                ...Array(rosterPlan.minimumSubstitutes).fill('SUBSTITUTES'),
                ...Array(rosterPlan.coaches).fill('COACH'),
            ] as VirtualLineupType[];
            setAssignments(Object.fromEntries(
                randomizedPlayers.slice(0, roles.length).map((player, index) => [player.id, roles[index]]),
            ));
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not randomize the player pool');
        } finally {
            setLoadingPool(false);
        }
    };
    const valid = sportId !== '' && draft.name.trim().length >= 3 && draft.nameRu.trim().length >= 3
        && draft.biography.trim().length >= 40 && draft.biographyRu.trim().length >= 40
        && draft.originCountry.length === 2 && Object.keys(assignments).length > 0 && rosterWithinLimits;

    return (
        <Dialog open={props.open} onClose={busy ? undefined : props.onClose} fullWidth maxWidth="lg">
            <DialogTitle>Add virtual team</DialogTitle>
            <DialogContent>
                {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField select required label="Virtual sport" value={sportId} onChange={e => selectSport(Number(e.target.value))}>
                        {props.sports.map(sport => <MenuItem key={sport.id} value={sport.id}>{sport.name}</MenuItem>)}
                    </TextField>
                    <Typography variant="subtitle2" color="text.secondary">Team identity and optional AI generation hints</Typography>
                    <VirtualCountrySelect
                        value={draft.originCountry}
                        onChange={value => update('originCountry', value)}
                        disabled={busy}
                        required
                        label="Team origin country"
                    />
                    <TextField
                        multiline
                        minRows={2}
                        label="Team generation description (optional)"
                        value={generationDescription}
                        onChange={event => setGenerationDescription(event.target.value)}
                        disabled={busy}
                        inputProps={{ maxLength: 2000 }}
                        helperText={`${generationDescription.length}/2000 · Used only when prepopulating with AI`}
                    />
                    <TextField required label="Name (English)" value={draft.name} onChange={e => update('name', e.target.value)} />
                    <TextField required label="Name (Russian)" value={draft.nameRu} onChange={e => update('nameRu', e.target.value)} />
                    <TextField required multiline minRows={3} label="Biography (English)" value={draft.biography} onChange={e => update('biography', e.target.value)} />
                    <TextField required multiline minRows={3} label="Biography (Russian)" value={draft.biographyRu} onChange={e => update('biographyRu', e.target.value)} />

                    {sportId !== '' ? <Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                            <Typography variant="h6" sx={{ flex: 1 }}>Available player pool</Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={randomizeRoster}
                                disabled={busy || loadingPool || poolTotal === 0 || !rosterPlan}
                            >
                                {Object.keys(assignments).length > 0 ? 'Randomize roster' : 'Select random roster'}
                            </Button>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">Select players from the unassigned pool. On save, substitutes fill vacant lineup positions and the oldest selected player becomes coach when no coach is specified.</Typography>
                        {rosterPlan ? <Typography variant="body2" sx={{ mt: 1 }} color={rosterComplete ? 'success.main' : 'text.secondary'}>
                            Starting lineup {assignmentCounts.STARTING_LINEUP}/{rosterPlan.startingLineup} · Substitutes {assignmentCounts.SUBSTITUTES}/{rosterPlan.minimumSubstitutes} minimum ({rosterPlan.maximumSubstitutes} max) · Coach {assignmentCounts.COACH}/{rosterPlan.coaches}
                        </Typography> : null}
                        {loadingPool ? <CircularProgress size={24} sx={{ mt: 2 }} /> : poolTotal === 0 ? (
                            <Typography color="text.secondary" sx={{ mt: 1 }}>No unassigned players are available.</Typography>
                        ) : <Table size="small" sx={{ mt: 1 }}>
                            <TableHead><TableRow><TableCell padding="checkbox" /><TableCell>Player</TableCell><TableCell>Country / year</TableCell><TableCell>Lineup role</TableCell></TableRow></TableHead>
                            <TableBody>{pool.map(player => {
                                const selected = Boolean(assignments[player.id]);
                                return <TableRow key={player.id} hover>
                                    <TableCell padding="checkbox"><Checkbox checked={selected} onChange={() => togglePlayer(player.id)} /></TableCell>
                                    <TableCell>{player.name}{player.nameRu ? ` / ${player.nameRu}` : ''}</TableCell>
                                    <TableCell>{player.originCountry} · {player.yearOfBirth}</TableCell>
                                    <TableCell><FormControl size="small" sx={{ minWidth: 180 }} disabled={!selected}>
                                        <InputLabel>Role</InputLabel>
                                        <Select label="Role" value={assignments[player.id] ?? 'STARTING_LINEUP'} onChange={e => changeLineupType(player.id, e.target.value as VirtualLineupType)}>
                                            {VIRTUAL_LINEUP_TYPES.map(role => <MenuItem key={role} value={role}>{virtualLineupLabel(role)}</MenuItem>)}
                                        </Select>
                                    </FormControl></TableCell>
                                </TableRow>;
                            })}</TableBody>
                        </Table>}
                        {poolTotal > PLAYER_POOL_PAGE_SIZE ? <Pagination
                            count={Math.ceil(poolTotal / PLAYER_POOL_PAGE_SIZE)}
                            page={poolPage + 1}
                            onChange={(_, value) => setPoolPage(value - 1)}
                            disabled={loadingPool || busy}
                            sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}
                        /> : null}
                    </Box> : null}

                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose} disabled={busy}>Cancel</Button>
                <Button variant="outlined" disabled={sportId === '' || busy || loadingPool} onClick={() => run(async () => setDraft(await previewVirtualTeam(
                    sportId as number,
                    poolPlayers(),
                    {
                        countryCode: draft.originCountry || undefined,
                        description: generationDescription.trim() || undefined,
                    },
                )))}>
                    {busy ? <CircularProgress size={20} /> : 'Prepopulate with AI'}
                </Button>
                <Button variant="contained" disabled={!valid || busy} onClick={() => run(async () => {
                    const created = await createVirtualTeam(sportId as number, { ...draft, poolPlayers: poolPlayers() });
                    props.onCreated(created); props.onClose();
                })}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}
